import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { AccountingEngine } from '../services/accountingEngine';
import { formatDate } from '../lib/dateUtils';

export const VoucherRegisterView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex, activeMenuLevel, selectedPeriod }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  
  const registerType = activeMenuLevel.split('_')[2]; // e.g., 'receipt'
  const title = registerType.charAt(0).toUpperCase() + registerType.slice(1) + ' Register';

  const vouchersData = useLiveQuery(async () => {
    if (!activeCompany || !selectedPeriod) return [];
    const typeMap: Record<string, string> = {
      'receipt': 'Receipt',
      'payment': 'Payment',
      'sales': 'Sales',
      'purchase': 'Purchase',
      'journal': 'Journal',
      'contra': 'Contra'
    };
    const vchType = typeMap[registerType] || 'Receipt';
    return await AccountingEngine.generateVoucherRegister(activeCompany.id!, vchType, selectedPeriod);
  }, [activeCompany, registerType, selectedPeriod]);

  const flatItems = vouchersData || [];
  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems && flatItems[selectedIndex] && handleItemSelect(flatItems[selectedIndex], selectedIndex));

  const totalDr = flatItems.reduce((sum, item) => sum + (Number(item.debit) || 0), 0);
  const totalCr = flatItems.reduce((sum, item) => sum + (Number(item.credit) || 0), 0);

  // Calculate period display (e.g., 1-Apr-26 to 30-Apr-26)
  let periodDisplay = '';
  if (selectedPeriod) {
    const [year, month] = selectedPeriod.split('-');
    const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = new Date(parseInt(year), parseInt(month), 0);
    periodDisplay = `${formatDate(startDate)} to ${formatDate(endDate)}`;
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Voucher Register</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-gray-300 z-10 shadow-sm">
        <span>List of All {title} Vouchers</span>
        <span>{periodDisplay}</span>
      </div>

      <div className="flex flex-col border-b border-gray-300 bg-white text-black font-bold px-2 py-1 shadow-sm">
        <div className="flex w-full">
          <div className="w-[80px]">Date</div>
          <div className="flex-1">Particulars</div>
          <div className="w-[120px]">Vch Type</div>
          <div className="w-[80px] text-right">Vch No.</div>
          <div className="w-[100px] text-right">Debit<br/><span className="font-normal text-[10px]">Amount</span></div>
          <div className="w-[100px] text-right">Credit<br/><span className="font-normal text-[10px]">Amount</span></div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin">
        {flatItems.map((item: any, idx: number) => (
          <div key={idx} onClick={() => handleItemSelect(item, idx)} className={`flex w-full px-2 py-[2px] cursor-pointer ${selectedIndex === idx ? 'bg-[#1e8585] text-white active-row' : 'text-black'}`}>
            <div className="w-[80px]">{formatDate(item.date)}</div>
            <div className="flex-1 italic font-semibold">{item.particulars}</div>
            <div className="w-[120px] font-semibold">{item.vchType}</div>
            <div className="w-[80px] text-right">{item.vchNo}</div>
            <div className="w-[100px] text-right font-semibold">{item.debit}</div>
            <div className="w-[100px] text-right font-semibold">{item.credit}</div>
          </div>
        ))}
        {flatItems.length === 0 && (
          <div className="p-4 text-center text-gray-500 italic">No vouchers found for this month.</div>
        )}
      </div>

      <div className="flex border-t border-gray-400 bg-white font-bold px-2 py-1 shadow-sm">
        <div className="flex w-full">
          <div className="w-[80px]"></div>
          <div className="flex-1 text-right pr-4">Total:</div>
          <div className="w-[120px]"></div>
          <div className="w-[80px]"></div>
          <div className="w-[100px] text-right">{totalDr > 0 ? totalDr.toFixed(2) : ''}</div>
          <div className="w-[100px] text-right">{totalCr > 0 ? totalCr.toFixed(2) : ''}</div>
        </div>
      </div>
    </div>
  );
};
